import { useState, useEffect, useRef, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PermissionGate } from '@/components/PermissionGate';
import BulkForecastReview from '@/components/procurement/BulkForecastReview';
import InsightsPanel from '@/components/procurement/InsightsPanel';
import InlineEdit from '@/components/procurement/InlineEdit';
import LogReceiptDrawer from '@/components/procurement/LogReceiptDrawer';
import ReceiptDrilldownModal from '@/components/procurement/ReceiptDrilldownModal';
import {
  Loader2, Sparkles, Truck, Package as PackageIcon, ShieldAlert, AlertOctagon,
  AlertTriangle, Factory, ClipboardCheck, Ship, MapPin, CheckCircle2, Zap, X,
  Filter, FileSpreadsheet, ArrowUp, ArrowDown, Minus, TrendingUp, FileText, Calendar,
  ChevronRight, ChevronDown, Link2, PackageCheck,
} from 'lucide-react';

const C = {
  primary: '#1E4E8C', primaryText: 'var(--tone-navy-text)', accent: '#F26B1F',
  slate: 'var(--text-primary)', slateMuted: 'var(--text-muted)',
  border: 'rgba(30,78,140,0.14)', surface: 'var(--card-bg)', surfaceAlt: 'var(--card-bg-alt)',
};

const STATUS = { DELAYED: '#EF4444', AT_RISK: '#F59E0B', ON_TRACK: '#22C55E', DONE: '#1E4E8C' };
const STAGES = [
  { key: 'Kickoff',       icon: PackageIcon,    color: 'var(--text-muted)' },
  { key: 'Engineering',   icon: ClipboardCheck, color: '#3B82F6' },
  { key: 'Manufacturing', icon: Factory,        color: '#8B5CF6' },
  { key: 'Inspection',    icon: ShieldAlert,    color: '#F59E0B' },
  { key: 'Dispatch',      icon: Ship,           color: '#F26B1F' },
  { key: 'In-Transit',    icon: Truck,          color: '#0EA5E9' },
  { key: 'At-Site',       icon: MapPin,         color: '#22C55E' },
];

const Delta = ({ v, invert }) => {
  if (v == null || v === 0) return <span className="inline-flex items-center text-[9px]" style={{ color: C.slateMuted }}><Minus className="w-2 h-2" />0</span>;
  const isBad = invert ? v < 0 : v > 0;
  const Icon = v > 0 ? ArrowUp : ArrowDown;
  return <span className="inline-flex items-center gap-0.5 text-[9px] font-mono" style={{ color: isBad ? '#EF4444' : '#22C55E' }}><Icon className="w-2 h-2" />{Math.abs(v)}</span>;
};

// The brand navy is too dark to read on the obsidian theme — labels use the
// theme-lifted tone while the card border keeps the hex (var() can't take an alpha suffix).
const TONE_TEXT = { '#1E4E8C': 'var(--tone-navy-text)' };

const KPI = ({ label, value, tone, icon: Icon, sub, delta, deltaInvert }) => (
  <div className="p-3 rounded-lg" style={{ background: C.surface, border: `1px solid ${tone}33` }}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1 text-[10px] uppercase font-semibold tracking-[0.08em]" style={{ color: TONE_TEXT[tone] || tone }}>
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      {delta !== undefined && <Delta v={delta} invert={deltaInvert} />}
    </div>
    <div className="mt-1 text-2xl font-bold leading-none" style={{ color: C.slate }}>{value}</div>
    {sub && <div className="mt-0.5 text-[10px]" style={{ color: C.slateMuted }}>{sub}</div>}
  </div>
);

// -----------------------------------------------------------
// Vendor drill-down modal
// -----------------------------------------------------------
function VendorDrilldown({ projectId, vendor, onClose }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState('');
  const [kind, setKind] = useState('call');
  const [contact, setContact] = useState({ name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/v1/projects/${projectId}/mci/control-tower/post-order/vendor/${encodeURIComponent(vendor)}`)
    .then(r => setData(r.data)).catch(() => setData({ packages: [], trend: [], log: [] }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, vendor]);

  const addLog = async () => {
    if (!note.trim()) return toast.warning('Enter a note');
    setSaving(true);
    try {
      await api.post(`/v1/projects/${projectId}/mci/control-tower/post-order/vendor/${encodeURIComponent(vendor)}/log`,
        { note, kind, contactName: contact.name, contactEmail: contact.email, contactPhone: contact.phone });
      setNote(''); load();
      toast.success('Contact log added');
    } catch (e) { toast.error('Failed to save'); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50" onClick={onClose} data-testid="vendor-drill">
      <div className="absolute right-0 top-0 h-full w-full max-w-2xl overflow-y-auto p-4"
        onClick={e => e.stopPropagation()} style={{ background: C.surface, borderLeft: `1px solid ${C.border}` }}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.08em] font-semibold" style={{ color: C.primaryText }}>Vendor Deep-Dive</div>
            <div className="text-lg font-bold" style={{ color: C.slate }}>{vendor}</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        {!data ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (
          <>
            {data.vendor && (
              <div className="grid grid-cols-4 gap-2 mb-3">
                <KPI label="POs" value={data.vendor.poCount} tone={C.primary} />
                <KPI label="On-Time" value={data.vendor.onTime} tone="#22C55E" />
                <KPI label="Delayed" value={data.vendor.delayed} tone="#EF4444" />
                <KPI label="OTD %" value={`${data.vendor.otdPercent}%`} tone={C.accent} />
              </div>
            )}
            {/* Weekly OTD trend */}
            {data.trend?.length > 0 && (
              <div className="rounded-lg p-3 mb-3" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}>
                <div className="text-[10px] uppercase font-semibold mb-1 flex items-center gap-1" style={{ color: C.primaryText }}>
                  <TrendingUp className="w-3 h-3" /> 4-Week OTD Trend
                </div>
                <div className="flex items-end gap-2 h-16">
                  {data.trend.map((t, i) => {
                    const total = t.onTime + t.delayed;
                    const otd = total ? (t.onTime / total) * 100 : 0;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center">
                        <div className="w-full rounded-t" style={{ height: `${Math.max(4, otd * 0.5)}px`, background: otd > 70 ? '#22C55E' : otd > 40 ? C.accent : '#EF4444' }} />
                        <div className="text-[8px] font-mono mt-1" style={{ color: C.slateMuted }}>W-{data.trend.length - i}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.primaryText }}>Packages ({data.packages?.length || 0})</div>
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {(data.packages || []).map(p => (
                <div key={p.mciLineNumber} className="p-2 rounded text-[11px]" style={{ background: C.surfaceAlt, borderLeft: `3px solid ${STATUS[p.status]}` }}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-bold" style={{ color: C.primaryText }}>{p.mciLineNumber}</span>
                    <span className="px-1.5 rounded text-white text-[9px] font-bold" style={{ background: STATUS[p.status] }}>{p.status}</span>
                  </div>
                  <div className="truncate" style={{ color: C.slate }}>{p.materialDescription}</div>
                  <div style={{ color: C.slateMuted }}>Stage: {p.currentStage} · Gate: {p.currentGate}</div>
                </div>
              ))}
            </div>

            {/* Contact log */}
            <div className="mt-3 pt-3 border-t" style={{ borderColor: C.border }}>
              <div className="text-[10px] uppercase font-semibold mb-2" style={{ color: C.accent }}>Vendor Contact Log</div>
              <div className="grid grid-cols-3 gap-1 mb-1">
                <select value={kind} onChange={e => setKind(e.target.value)} className="text-[10px] p-1 rounded border" style={{ borderColor: C.border, background: C.surfaceAlt }} data-testid="log-kind">
                  {['call', 'email', 'meeting', 'expedite'].map(x => <option key={x}>{x}</option>)}
                </select>
                <input placeholder="Contact name" value={contact.name} onChange={e => setContact({ ...contact, name: e.target.value })}
                  className="text-[10px] p-1 rounded border col-span-2" style={{ borderColor: C.border, background: C.surfaceAlt }} />
                <input placeholder="Email" value={contact.email} onChange={e => setContact({ ...contact, email: e.target.value })}
                  className="text-[10px] p-1 rounded border" style={{ borderColor: C.border, background: C.surfaceAlt }} />
                <input placeholder="Phone" value={contact.phone} onChange={e => setContact({ ...contact, phone: e.target.value })}
                  className="text-[10px] p-1 rounded border col-span-2" style={{ borderColor: C.border, background: C.surfaceAlt }} />
              </div>
              <textarea rows={2} placeholder="Follow-up note" value={note} onChange={e => setNote(e.target.value)}
                className="w-full p-1.5 text-[11px] rounded border" style={{ borderColor: C.border, background: C.surfaceAlt }}
                data-testid="log-note" />
              <Button size="sm" onClick={addLog} disabled={saving} className="h-6 text-[10px] mt-1 text-white" style={{ background: C.accent }} data-testid="log-save">
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Add to Log
              </Button>
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {(data.log || []).map((l, i) => (
                  <div key={i} className="p-1.5 rounded text-[10px]" style={{ background: C.surfaceAlt, borderLeft: `2px solid ${C.primary}` }} data-testid={`log-entry-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold uppercase" style={{ color: C.primaryText }}>{l.kind}</span>
                      <span style={{ color: C.slateMuted }}>{new Date(l.ts).toLocaleString()}</span>
                    </div>
                    <div style={{ color: C.slate }}>{l.note}</div>
                    {(l.contactName || l.contactEmail || l.contactPhone) && (
                      <div style={{ color: C.slateMuted }}>{l.contactName} · {l.contactEmail} · {l.contactPhone}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------
// Exception drill-down modal
// -----------------------------------------------------------
function ExceptionDrilldown({ type, items, onClose }) {
  const titles = { inspectionFail: 'Inspection Fails', shippingDelay: 'Shipping Delays', customsHold: 'Customs Holds', grnPending: 'GRN Pending' };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center" onClick={onClose} data-testid="exception-drill">
      <div className="w-full max-w-xl max-h-[80vh] p-4 rounded-lg overflow-y-auto" onClick={e => e.stopPropagation()}
        style={{ background: C.surface, border: `1px solid ${C.border}` }}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.08em] font-semibold" style={{ color: C.accent }}>{titles[type]}</div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        {items.length === 0 ? <div className="text-center py-6 text-xs" style={{ color: C.slateMuted }}>No items.</div> :
          <div className="space-y-1">
            {items.map((it, i) => (
              <div key={i} className="p-2 rounded text-[11px]" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}>
                <div className="flex items-center justify-between">
                  <span className="font-mono font-bold" style={{ color: C.primaryText }}>{it.mci}</span>
                  {it.vendor && <span style={{ color: C.slateMuted }}>{it.vendor}</span>}
                </div>
                {(it.gate || it.tracking || it.remarks || it.planned) && (
                  <div style={{ color: C.slateMuted }} className="mt-0.5">
                    {it.gate && <span>Gate: {it.gate} · </span>}
                    {it.tracking && <span>Tracking: {it.tracking} · </span>}
                    {it.remarks && <span>Remarks: {it.remarks} · </span>}
                    {it.planned && <span>Planned: {it.planned.slice(0, 10)}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>}
      </div>
    </div>
  );
}

function MiniTimeline({ upcoming }) {
  const today = new Date();
  const cells = useMemo(() => {
    const arr = Array.from({ length: 30 }, (_, i) => {
      const d = new Date(today); d.setDate(today.getDate() + i);
      return { date: d, items: [] };
    });
    (upcoming || []).forEach(u => {
      const d = new Date(u.date);
      const diff = Math.floor((d - today) / (1000 * 60 * 60 * 24));
      if (diff >= 0 && diff < 30) arr[diff].items.push(u);
    });
    return arr;
    // eslint-disable-next-line
  }, [upcoming]);
  const max = Math.max(1, ...cells.map(c => c.items.length));
  return (
    <div className="rounded-lg p-3 mb-3" style={{ background: C.surface, border: `1px solid ${C.border}` }}
      data-testid="ct-post-timeline">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.08em] font-semibold flex items-center gap-1" style={{ color: C.primaryText }}>
          <Calendar className="w-3 h-3" /> Next 30 Days · Post-Order Milestones
        </div>
        <div className="text-[10px]" style={{ color: C.slateMuted }}>{(upcoming || []).length} upcoming gates</div>
      </div>
      <div className="flex items-end gap-[3px] h-16">
        {cells.map((c, i) => {
          const h = c.items.length ? (18 + (c.items.length / max) * 44) : 4;
          const hot = c.items.length >= max * 0.66;
          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end relative" style={{ height: '100%' }}>
              <div className="w-full rounded-t transition-all"
                style={{ height: h, background: c.items.length ? (hot ? C.accent : C.primary) : 'var(--border-color)',
                          opacity: c.items.length ? 0.9 : 0.5 }}
                title={`${c.date.toLocaleDateString()} · ${c.items.length} gate(s)`} />
              {i % 5 === 0 && (
                <div className="absolute -bottom-4 text-[8px] font-mono" style={{ color: C.slateMuted }}>
                  {c.date.getDate()}/{c.date.getMonth() + 1}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-6 max-h-32 overflow-y-auto space-y-1">
        {(upcoming || []).slice(0, 8).map((u, i) => (
          <div key={i} className="flex items-center justify-between text-[11px] py-1" style={{ borderBottom: `1px solid ${C.border}` }}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-[10px] font-bold" style={{ color: C.primaryText }}>{u.mciLineNumber}</span>
              <span className="truncate" style={{ color: C.slate, maxWidth: 320 }}>{u.materialDescription}</span>
            </div>
            <div className="flex items-center gap-3 text-[10px]" style={{ color: C.slateMuted }}>
              <span className="px-1.5 py-0.5 rounded" style={{ background: `${C.accent}14`, color: C.accent }}>{u.gate}</span>
              <span className="font-mono">{new Date(u.date).toLocaleDateString()}</span>
            </div>
          </div>
        ))}
        {(upcoming || []).length === 0 && <div className="text-[11px] text-center py-3" style={{ color: C.slateMuted }}>No post-order gates due in the next 30 days.</div>}
      </div>
    </div>
  );
}

export default function ControlTowerPostOrder({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  // Receipt drilldown modal state — opened by clicking a Status/Gate pill
  // in the Post-Order Table. `null` = closed; `{mci, receipt, remaining, unit}` = open.
  const [receiptDrilldown, setReceiptDrilldown] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [exception, setException] = useState(null);
  const [filters, setFilters] = useState({ discipline: '', vendor: '', priority: '', search: '', materialCategory: 'ALL' });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [expandedStages, setExpandedStages] = useState({});
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'table'
  const ALL_COLS = [
    { k: 'mciLineNumber', label: 'MCI' }, { k: 'mrNumber', label: 'MR#' },
    { k: 'poNumber', label: 'PO # · L' }, { k: 'vendor', label: 'Vendor' },
    { k: 'materialDescription', label: 'Description' }, { k: 'discipline', label: 'Discipline' },
    { k: 'priority', label: 'Priority' }, { k: 'currentGate', label: 'Current Gate' },
    { k: 'status', label: 'Status' }, { k: 'poValue', label: 'Value' },
    { k: 'actions', label: 'Actions' },
  ];
  const [visibleCols, setVisibleCols] = useState(() => {
    try { return JSON.parse(localStorage.getItem('po-table-cols')) || ALL_COLS.map(c => c.k); }
    catch { return ALL_COLS.map(c => c.k); }
  });
  const [showColPicker, setShowColPicker] = useState(false);
  const toggleCol = (k) => {
    const next = visibleCols.includes(k) ? visibleCols.filter(x => x !== k) : [...visibleCols, k];
    setVisibleCols(next);
    localStorage.setItem('po-table-cols', JSON.stringify(next));
  };
  const isVis = (k) => visibleCols.includes(k);
  const [dragged, setDragged] = useState(null);
  const [drilldownReceipts, setDrilldownReceipts] = useState(null);
  const [laneMode, setLaneMode] = useState('7');
  const debounceRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== 'ALL') params.append(k, v); });
      const { data: d } = await api.get(`/v1/projects/${projectId}/mci/control-tower/post-order?${params}`);
      setData(d);
    } catch (e) { toast.error('Failed to load Post-Order Control Tower'); }
    setLoading(false);
  };

  const exportXlsx = async () => {
    try {
      const r = await api.get(`/v1/projects/${projectId}/mci/control-tower/export/xlsx?phase=POST`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = 'post-order-control-tower.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  const openPostOrderBulk = async () => {
    setBulkBusy(true);
    try {
      const params = new URLSearchParams();
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.discipline) params.append('discipline', filters.discipline);
      const { data: r } = await api.post(
        `/v1/projects/${projectId}/mci/control-tower/gate-forecast/register-cognara-suggest?${params}`);
      // Filter suggestions to POST phase only
      const rows = (r.rows || []).map(row => ({
        ...row, suggestions: (row.suggestions || []).filter(s => s.phase === 'POST'),
      })).filter(r => r.suggestions.length);
      if (!rows.length) { toast.info('No open post-order gates to forecast.'); setBulkBusy(false); return; }
      setBulkRows(rows);
      setBulkOpen(true);
    } catch (e) { toast.error('Cognara register-wide suggest failed'); }
    setBulkBusy(false);
  };

  const raiseExpedite = async (mci) => {
    try {
      const r = await api.post(`/v1/projects/${projectId}/mci/control-tower/actions/expedite`,
        { mciLineNumber: mci, priority: 'HIGH', dueDays: 7, reason: 'Raised from Post-Order Control Tower' });
      toast.success(`Expedite raised · ${r.data.raid.raidNumber}`);
    } catch (e) { toast.error('Expedite failed'); }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [projectId, filters]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener('mci-register-updated', h);
    return () => window.removeEventListener('mci-register-updated', h);
    // eslint-disable-next-line
  }, [projectId]);

  if (loading && !data) return (
    <div className="p-12 text-center text-sm" style={{ color: C.slateMuted }}>
      <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" style={{ color: C.primaryText }} />
      Loading Post-Order Control Tower…
    </div>
  );
  if (!data) return null;
  const k = data.kpis; const dd = data.deltas || {};

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="ct-postorder" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="col-span-12 lg:col-span-9 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
          <KPI label="Total POs"     value={k.totalPOs}   tone={C.primary} icon={PackageIcon} delta={dd.totalPOs} />
          <KPI label="On-Time"       value={k.onTime}     tone="#22C55E" icon={CheckCircle2} delta={dd.onTime} />
          <KPI label="At-Risk"       value={k.atRisk}     tone="#F59E0B" icon={AlertTriangle} delta={dd.atRisk} deltaInvert />
          <KPI label="Delayed"       value={k.delayed}    tone="#EF4444" icon={AlertOctagon} delta={dd.delayed} deltaInvert />
          <KPI label="In-Transit"    value={k.inTransit}  tone="#0EA5E9" icon={Truck} delta={dd.inTransit} />
          <KPI label="At-Site"       value={k.atSite}     tone={C.accent} icon={MapPin} delta={dd.atSite} />
          <KPI label="Lead Time (d)" value={k.weightedLeadTimeDays} tone={C.primary} sub="PO → Site" delta={dd.weightedLeadTimeDays} deltaInvert />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap p-2 rounded-lg" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }} data-testid="ctp-filters">
          <Filter className="w-3 h-3" style={{ color: C.primaryText }} />
          <input placeholder="Search MCI/desc/vendor…" value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })}
            className="text-[11px] px-2 py-1 rounded border" style={{ borderColor: C.border, background: C.surface, width: 180 }} />
          <select value={filters.vendor} onChange={e => setFilters({ ...filters, vendor: e.target.value })}
            className="text-[11px] px-2 py-1 rounded border" style={{ borderColor: C.border, background: C.surface }}>
            <option value="">All Vendors</option>
            {[...new Set(data.packages.map(p => p.vendor))].filter(Boolean).map(v => <option key={v}>{v}</option>)}
          </select>
          <select value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value })}
            className="text-[11px] px-2 py-1 rounded border" style={{ borderColor: C.border, background: C.surface }}>
            <option value="">All Priorities</option>
            {['CRITICAL', 'HIGH', 'NORMAL', 'LOW'].map(x => <option key={x}>{x}</option>)}
          </select>
          <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: C.border }}
            data-testid="ct-post-cat-filter">
            {['ALL', 'BULK', 'TAGGED'].map(c => (
              <button key={c} onClick={() => setFilters({ ...filters, materialCategory: c })}
                className="px-2.5 py-1 text-[10px] font-semibold transition-colors"
                style={{ background: filters.materialCategory === c ? '#1E4E8C' : C.surface,
                          color: filters.materialCategory === c ? '#ffffff' : C.slateMuted }}
                data-testid={`ct-post-cat-${c.toLowerCase()}`}>{c}</button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[10px]" style={{ color: C.slateMuted }}>{data.packages.length} POs</span>
            <PermissionGate module="MCI" action="export">
              <Button size="sm" variant="outline" onClick={exportXlsx} className="h-6 text-[10px]" data-testid="ctp-export-btn">
                <FileSpreadsheet className="w-3 h-3 mr-1" /> Excel
              </Button>
            </PermissionGate>
          </div>
        </div>

        {/* Kanban with drag-and-drop */}
        {viewMode === 'table' ? (
          <div className="rounded-lg p-3 overflow-x-auto relative" style={{ background: C.surface, border: `1px solid ${C.border}` }}
            data-testid="ct-post-table">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase font-bold" style={{ color: C.primaryText }}>Post-Order Table · {(data.packages || []).length} rows</div>
              <div className="relative">
                <button onClick={() => setShowColPicker(!showColPicker)}
                  className="text-[10px] px-2 py-1 rounded border font-semibold"
                  style={{ borderColor: C.border, color: C.primaryText, background: C.surfaceAlt }}
                  data-testid="col-chooser-btn">Columns · {visibleCols.length}/{ALL_COLS.length}</button>
                {showColPicker && (
                  <div className="absolute right-0 top-full mt-1 z-10 rounded border p-2 shadow-lg min-w-[160px]"
                    style={{ background: C.surface, borderColor: C.border }}
                    data-testid="col-chooser-menu">
                    {ALL_COLS.map(c => (
                      <label key={c.k} className="flex items-center gap-2 py-0.5 text-[11px] cursor-pointer"
                        style={{ color: C.slate }}>
                        <input type="checkbox" checked={isVis(c.k)} onChange={() => toggleCol(c.k)}
                          data-testid={`col-toggle-${c.k}`} />
                        {c.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <table className="w-full text-[11px]">
              <thead>
                <tr style={{ background: C.surfaceAlt }}>
                  {ALL_COLS.filter(c => isVis(c.k)).map(c =>
                    <th key={c.k} className="px-2 py-2 text-left uppercase text-[10px] font-bold" style={{ color: C.slateMuted }}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {(data.packages || []).map(p => {
                  const orderedQty  = Number(p.quantity ?? 0);
                  const grnDone     = Number(p.receiptTotals?.grn ?? 0);
                  const remaining   = Math.max(0, orderedQty - grnDone);
                  const fullyDelivered = orderedQty > 0 && remaining <= 0.001;
                  const accepted = (p.receipts || []).filter(r => (r.acceptedQty || 0) > 0);
                  // Prefer the backend-computed `currentGate` (which is the
                  // stage the MCI is CURRENTLY active in — same one the
                  // Kanban board uses) so the open-balance pill label
                  // matches the Kanban column. Fallback to first non-DONE
                  // gate for MCIs missing currentGate.
                  const openGateName = p.currentGate
                    || (p.gates || []).find(g => g.status !== 'DONE')?.name
                    || (p.gates || []).slice(-1)[0]?.name;
                  const openGate = (p.gates || []).find(g => g.name === openGateName)
                    || (p.gates || []).find(g => g.status !== 'DONE')
                    || (p.gates || []).slice(-1)[0];
                  const openGateStatus = openGate?.status || 'ON_TRACK';
                  return (
                    <tr key={p.mciLineNumber} onClick={() => setSelected(p)}
                      className="cursor-pointer hover:bg-black/5"
                      style={{ borderTop: `1px solid ${C.border}` }}
                      data-testid={`post-table-row-${p.mciLineNumber}`}>
                      {isVis('mciLineNumber') && <td className="px-2 py-1.5 font-mono font-bold" style={{ color: C.primaryText }}>{p.mciLineNumber}</td>}
                      {isVis('mrNumber') && <td className="px-2 py-1.5 font-mono text-[10px]" style={{ color: C.slateMuted }}>{p.mrNumber || '—'}</td>}
                      {isVis('poNumber') && <td className="px-2 py-1.5 font-mono text-[10px]" style={{ color: C.slateMuted }}>
                        {p.poNumber ? `${p.poNumber} · ${p.poLineItem || 'L—'}` : '—'}
                        {p.quantity != null && (
                          <div className="text-[9px]" style={{ color: C.slateMuted }}>
                            {p.quantity} {p.unit || ''}
                          </div>
                        )}
                      </td>}
                      {isVis('vendor') && <td className="px-2 py-1.5" style={{ color: C.slate }}>{p.vendor || '—'}</td>}
                      {isVis('materialDescription') && <td className="px-2 py-1.5 max-w-[200px] truncate" style={{ color: C.slate }} title={p.materialDescription}>{p.materialDescription}</td>}
                      {isVis('discipline') && <td className="px-2 py-1.5 text-[10px]" style={{ color: C.slateMuted }}>{p.discipline}</td>}
                      {isVis('priority') && <td className="px-2 py-1.5 text-center text-[10px] font-bold"
                        style={{ color: p.priority === 'CRITICAL' ? C.accent : p.priority === 'HIGH' ? '#EF4444' : C.slateMuted }}>{p.priority}</td>}
                      {isVis('currentGate') && <td className="px-2 py-1.5 text-[10px]" style={{ color: C.slate }}>
                        {accepted.length === 0 && !fullyDelivered && (
                          <span>{openGateName || '—'}</span>
                        )}
                        {accepted.length > 0 && (
                          <div className="flex flex-col gap-0.5" data-testid={`table-buckets-${p.mciLineNumber}`}>
                            {accepted.map(r => (
                              <span key={r.receiptNumber} className="text-[9.5px]"
                                title={`Accepted ${r.acceptedQty} ${r.unit || p.unit || ''} · GRN complete · ${r.acceptedLocation || ''}`}>
                                <b style={{ color: 'var(--tone-green-text)' }}>GRN</b>
                                <span style={{ color: C.slateMuted }}> · {r.acceptedQty} {r.unit || p.unit || ''}</span>
                              </span>
                            ))}
                            {!fullyDelivered && (
                              <span className="text-[9.5px]"
                                title={`${remaining} ${p.unit || ''} still due · currently tracking ${openGateName}`}>
                                <b style={{ color: 'var(--tone-amber-text)' }}>{openGateName || '—'}</b>
                                <span style={{ color: C.slateMuted }}> · {remaining} {p.unit || ''}</span>
                              </span>
                            )}
                          </div>
                        )}
                      </td>}
                      {isVis('status') && <td className="px-2 py-1.5">
                        <div className="flex flex-col gap-0.5" data-testid={`table-status-${p.mciLineNumber}`}>
                          {/* One green DONE pill per accepted receipt — click for scan-chain drilldown */}
                          {accepted.map(r => (
                            <button key={r.receiptNumber}
                              onClick={e => { e.stopPropagation();
                                setReceiptDrilldown({ mci: p.mciLineNumber, poNumber: p.poNumber, poLineItem: p.poLineItem,
                                                       receipt: r, unit: p.unit }); }}
                              className="px-1.5 py-0.5 rounded text-white text-[8.5px] font-bold whitespace-nowrap hover:opacity-90 text-left cursor-pointer"
                              style={{ background: '#10B981' }}
                              title={`${r.receiptNumber} · ${r.acceptedQty} ${r.unit || p.unit || ''} — click to see scan chain`}
                              data-testid={`pill-receipt-${p.mciLineNumber}-${r.receiptNumber}`}>
                              GRN · {r.acceptedQty}{r.unit || p.unit ? ` ${r.unit || p.unit}` : ''}
                            </button>
                          ))}
                          {/* One orange OPEN-BALANCE pill — click to open info modal */}
                          {!fullyDelivered && (
                            <button
                              onClick={e => { e.stopPropagation();
                                setReceiptDrilldown({ mci: p.mciLineNumber, poNumber: p.poNumber, poLineItem: p.poLineItem,
                                                       receipt: null, remaining, unit: p.unit }); }}
                              className="px-1.5 py-0.5 rounded text-white text-[8.5px] font-bold whitespace-nowrap hover:opacity-90 text-left cursor-pointer"
                              style={{ background: STATUS[openGateStatus] || '#F59E0B' }}
                              title={`${remaining} ${p.unit || ''} open · currently tracking ${openGateName} — click for details`}
                              data-testid={`pill-open-${p.mciLineNumber}`}>
                              {openGateName?.slice(0, 12) || 'OPEN'} · {remaining}{p.unit ? ` ${p.unit}` : ''}
                            </button>
                          )}
                          {accepted.length === 0 && fullyDelivered && (
                            <span className="px-1.5 py-0.5 rounded text-white text-[8.5px] font-bold"
                              style={{ background: STATUS[p.status] || 'var(--text-muted)' }}>{p.status.replace('_',' ')}</span>
                          )}
                        </div>
                      </td>}
                      {isVis('poValue') && <td className="px-2 py-1.5 text-right font-mono" style={{ color: C.accent }}>{p.poValue ? `$${Math.round(p.poValue/1000)}k` : '—'}</td>}
                      {isVis('actions') && <td className="px-2 py-1.5 text-center">
                        <button onClick={e => { e.stopPropagation(); raiseExpedite(p.mciLineNumber); }}
                          className="text-[9px] font-bold" style={{ color: C.accent }}
                          data-testid={`expedite-${p.mciLineNumber}`}>Expedite</button>
                      </td>}
                    </tr>
                  );
                })}
                {(data.packages || []).length === 0 && (
                  <tr><td colSpan={visibleCols.length} className="py-10 text-center text-sm" style={{ color: C.slateMuted }}>
                    No packages match the current filters.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : null}
        {viewMode === 'kanban' ? (
        <div className="rounded-lg p-3" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="ct-kanban">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.08em] font-semibold" style={{ color: C.primaryText }}>
              {laneMode === '7' ? '10-Gate Pipeline · 7 Kanban Stages' : '10 Canonical Gate Lanes'} · Drag to move
            </div>
            <div className="flex rounded overflow-hidden" style={{ border: `1px solid ${C.border}` }}
              data-testid="ct-post-view-toggle">
              <button onClick={() => setViewMode('kanban')} data-testid="view-kanban"
                className="px-2 py-0.5 text-[10px] font-semibold"
                style={{ background: viewMode === 'kanban' ? '#1E4E8C' : C.surface,
                          color: viewMode === 'kanban' ? '#ffffff' : C.slateMuted }}>Kanban</button>
              <button onClick={() => setViewMode('table')} data-testid="view-table"
                className="px-2 py-0.5 text-[10px] font-semibold"
                style={{ background: viewMode === 'table' ? '#1E4E8C' : C.surface,
                          color: viewMode === 'table' ? '#ffffff' : C.slateMuted }}>Table</button>
            </div>
            <div className="flex rounded overflow-hidden" style={{ border: `1px solid ${C.border}` }}>
              <button onClick={() => setLaneMode('7')} className="px-2 py-0.5 text-[10px] font-semibold" data-testid="lane-7"
                style={{ background: laneMode === '7' ? C.primaryText : C.surface, color: laneMode === '7' ? '#fff' : C.slateMuted }}>7 Stages</button>
              <button onClick={() => setLaneMode('10')} className="px-2 py-0.5 text-[10px] font-semibold" data-testid="lane-10"
                style={{ background: laneMode === '10' ? C.primaryText : C.surface, color: laneMode === '10' ? '#fff' : C.slateMuted }}>10 Gates</button>
            </div>
          </div>
          {laneMode === '7' ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 overflow-x-auto">
            {STAGES.map(stage => {
              const items = data.kanban[stage.key] || [];
              const Icon = stage.icon;
              return (
                <div key={stage.key}
                  onDragOver={e => e.preventDefault()}
                  onDrop={async (e) => {
                    e.preventDefault();
                    if (dragged && dragged.from !== stage.key) {
                      try {
                        await api.put(`/v1/projects/${projectId}/mci/control-tower/post-order/kanban/move`,
                          { mciLineNumber: dragged.pkg.mciLineNumber, toStage: stage.key });
                        toast.success(`${dragged.pkg.mciLineNumber} moved to ${stage.key}`);
                        load();
                      } catch (err) { toast.error('Move failed'); }
                    }
                    setDragged(null);
                  }}
                  className="rounded-md min-h-[220px]" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}
                  data-testid={`kanban-${stage.key.toLowerCase()}`}>
                  <div className="flex items-center justify-between px-2 py-1.5 border-b" style={{ borderColor: C.border }}>
                    <div className="flex items-center gap-1 text-[10px] font-semibold uppercase" style={{ color: stage.color }}>
                      <Icon className="w-3 h-3" /> {stage.key}
                    </div>
                    <span className="text-[10px] font-mono px-1.5 rounded" style={{ background: `${stage.color}22`, color: stage.color }}>{items.length}</span>
                  </div>
                  <div className="p-1.5 space-y-1 max-h-72 overflow-y-auto">
                    {items.length === 0 && <div className="text-[9px] text-center py-4" style={{ color: C.slateMuted }}>—</div>}
                    {(expandedStages[stage.key] ? items : items.slice(0, 15)).map(p => (
                      <div key={p.mciLineNumber} draggable
                        onDragStart={() => setDragged({ pkg: p, from: stage.key })}
                        onClick={() => setSelected(p)}
                        className="p-1.5 rounded cursor-move text-[10px]"
                        style={{ background: C.surface, border: `1px solid ${STATUS[p.status] || C.border}55`,
                                  borderLeft: `3px solid ${STATUS[p.status] || C.border}` }}
                        data-testid={`kanban-card-${p.mciLineNumber}`}>
                        <div className="font-mono font-bold" style={{ color: C.primaryText }}>{p.mciLineNumber}</div>
                        <div className="truncate" style={{ color: C.slate }}>{p.materialDescription}</div>
                        <div className="flex items-center justify-between mt-0.5" style={{ color: C.slateMuted }}>
                          <span className="truncate max-w-[90px]">{p.vendor}</span>
                          <span className="px-1 rounded text-white text-[9px] font-bold" style={{ background: STATUS[p.status] }}>{p.status.slice(0, 3)}</span>
                        </div>
                        {p.defaultReceiptLocation && (
                          <div className="flex items-center gap-0.5 mt-0.5" title={`Delivery / GRN / IQC location: ${p.defaultReceiptLocation}`}>
                            <MapPin className="w-2.5 h-2.5" style={{ color: C.primaryText }} />
                            <span className="text-[9px] truncate" style={{ color: C.primaryText }}>{p.defaultReceiptLocation}</span>
                          </div>
                        )}
                        {p.receiptTotals && (p.receiptTotals.grn > 0 || p.receiptTotals.pending > 0 || p.receiptTotals.accepted > 0 || p.receiptTotals.rejected > 0) && (
                          <div className="flex flex-wrap gap-0.5 mt-1 pt-1" style={{ borderTop: `1px dashed ${C.border}` }}
                            title={`GRN ${p.receiptTotals.grn} · Pending IQC ${p.receiptTotals.pending} · Accepted ${p.receiptTotals.accepted} · Rejected ${p.receiptTotals.rejected}`}
                            data-testid={`kanban-receipt-chips-${p.mciLineNumber}`}>
                            {p.receiptTotals.grn > 0 && (
                              <span className="px-1 rounded font-mono text-[8.5px] font-bold" style={{ background: 'var(--tone-blue-bg)', color: 'var(--tone-blue-text)' }}>GRN {p.receiptTotals.grn}</span>
                            )}
                            {p.receiptTotals.pending > 0 && (
                              <span className="px-1 rounded font-mono text-[8.5px] font-bold" style={{ background: 'var(--tone-amber-bg)', color: 'var(--tone-amber-text)' }}>PEND {p.receiptTotals.pending}</span>
                            )}
                            {p.receiptTotals.accepted > 0 && (
                              <span className="px-1 rounded font-mono text-[8.5px] font-bold" style={{ background: 'var(--tone-green-bg)', color: 'var(--tone-green-text)' }}>OK {p.receiptTotals.accepted}</span>
                            )}
                            {p.receiptTotals.rejected > 0 && (
                              <span className="px-1 rounded font-mono text-[8.5px] font-bold" style={{ background: 'var(--tone-red-bg)', color: 'var(--tone-red-text)' }}>REJ {p.receiptTotals.rejected}</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {items.length > 15 && (
                      <button
                        onClick={() => setExpandedStages({ ...expandedStages, [stage.key]: !expandedStages[stage.key] })}
                        className="w-full text-[10px] font-bold py-1 rounded"
                        style={{ background: `${C.primary}14`, color: C.primaryText }}
                        data-testid={`show-more-${stage.key}`}>
                        {expandedStages[stage.key] ? '↑ Show less' : `+${items.length - 15} more — Show all`}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          ) : (
          <div className="flex gap-2 overflow-x-auto pb-2" data-testid="ct-10-lanes">
            {(data.canonicalGates || []).map(gateName => {
              const items = (data.tenLanes || {})[gateName] || [];
              return (
                <div key={gateName} className="min-w-[180px] rounded-md" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}>
                  <div className="px-2 py-1.5 border-b flex items-center justify-between" style={{ borderColor: C.border }}>
                    <div className="text-[10px] font-semibold uppercase truncate" style={{ color: C.primaryText }}>{gateName}</div>
                    <span className="text-[10px] font-mono px-1.5 rounded" style={{ background: `${C.primary}22`, color: C.primaryText }}>{items.length}</span>
                  </div>
                  <div className="p-1.5 space-y-1 max-h-72 overflow-y-auto">
                    {items.length === 0 && <div className="text-[9px] text-center py-4" style={{ color: C.slateMuted }}>—</div>}
                    {items.slice(0, 20).map(p => (
                      <div key={p.mciLineNumber} onClick={() => setSelected(p)}
                        className="p-1.5 rounded cursor-pointer text-[10px]"
                        style={{ background: C.surface, borderLeft: `3px solid ${STATUS[p.status] || C.border}` }}>
                        <div className="font-mono font-bold" style={{ color: C.primaryText }}>{p.mciLineNumber}</div>
                        <div className="truncate" style={{ color: C.slate }}>{p.vendor}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
        ) : null}

        {/* Vendor + Exceptions */}
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 lg:col-span-8 rounded-lg p-3" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="ct-vendors">
            <div className="text-[10px] uppercase tracking-[0.08em] font-semibold mb-2" style={{ color: C.primaryText }}>Vendor Expediting · Click for drill-down</div>
            <table className="w-full text-[11px]">
              <thead>
                <tr style={{ background: `${C.primary}08` }}>
                  <th className="p-1.5 text-left" style={{ color: C.slateMuted }}>Vendor</th>
                  <th className="p-1.5 text-right" style={{ color: C.slateMuted }}>POs</th>
                  <th className="p-1.5 text-right" style={{ color: C.slateMuted }}>On-Time</th>
                  <th className="p-1.5 text-right" style={{ color: C.slateMuted }}>Delayed</th>
                  <th className="p-1.5 text-right" style={{ color: C.slateMuted }}>OTD %</th>
                  <th className="p-1.5 text-right" style={{ color: C.slateMuted }}>Value ($)</th>
                </tr>
              </thead>
              <tbody>
                {data.vendors.length === 0 && <tr><td colSpan={6} className="p-4 text-center" style={{ color: C.slateMuted }}>No vendors.</td></tr>}
                {data.vendors.map(v => (
                  <tr key={v.vendor} className="cursor-pointer hover:bg-blue-50/40" onClick={() => setVendor(v.vendor)}
                    data-testid={`vendor-row-${v.vendor.replace(/\s/g, '-')}`} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td className="p-1.5 font-semibold" style={{ color: C.primaryText }}>{v.vendor}</td>
                    <td className="p-1.5 text-right font-mono" style={{ color: C.slate }}>{v.poCount}</td>
                    <td className="p-1.5 text-right font-mono" style={{ color: '#22C55E' }}>{v.onTime}</td>
                    <td className="p-1.5 text-right font-mono" style={{ color: '#EF4444' }}>{v.delayed}</td>
                    <td className="p-1.5 text-right font-mono font-bold" style={{ color: v.otdPercent < 60 ? '#EF4444' : v.otdPercent < 85 ? C.accent : '#22C55E' }}>{v.otdPercent}%</td>
                    <td className="p-1.5 text-right font-mono" style={{ color: C.slate }}>{Number(v.totalValue || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="col-span-12 lg:col-span-4 rounded-lg p-3" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="ct-exceptions">
            <div className="text-[10px] uppercase tracking-[0.08em] font-semibold mb-2" style={{ color: C.accent }}>Exception Analytics · Click for detail</div>
            <div className="space-y-2">
              {Object.entries(data.exceptions).map(([key, val]) => (
                <div key={key} className="flex items-center justify-between p-2 rounded cursor-pointer hover:bg-blue-50/40"
                  onClick={() => setException(key)}
                  style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}
                  data-testid={`exception-${key}`}>
                  <div className="text-[11px] capitalize" style={{ color: C.slate }}>{key.replace(/([A-Z])/g, ' $1')}</div>
                  <div className="text-lg font-bold" style={{ color: val > 0 ? C.accent : C.slateMuted }}>{val}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="col-span-12 lg:col-span-3">
        <InsightsPanel projectId={projectId} phase="POST_ORDER" color={C.primary} testId="ct-post-insights" />
      </div>

      {/* Next-30-Days Post-Order milestone density (moved to bottom per user) */}
      <div className="col-span-12">
        {data.upcoming && <MiniTimeline upcoming={data.upcoming} />}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setSelected(null)}>
          <div className="absolute right-0 top-0 h-full w-full max-w-lg overflow-y-auto p-4" onClick={e => e.stopPropagation()}
            style={{ background: C.surface, borderLeft: `1px solid ${C.border}` }}>
            <div className="flex justify-between mb-3">
              <div>
                <div className="font-mono font-bold" style={{ color: C.primaryText }}>{selected.mciLineNumber}</div>
                <div className="text-xs" style={{ color: C.slate }}>{selected.materialDescription}</div>
                <div className="text-[10px]" style={{ color: C.slateMuted }}>PO: {selected.poNumber || '—'} · Vendor: {selected.vendor} · ${Number(selected.poValue || 0).toLocaleString()}</div>
              </div>
              <div className="flex items-start gap-2">
                <button onClick={() => setDrilldownReceipts(selected)}
                  className="px-2 py-1 rounded text-[10px] font-bold text-white" style={{ background: '#F26B1F' }}
                  data-testid={`drilldown-log-receipt-${selected.mciLineNumber}`}
                  title="Log GRN + IQC against this PO line">
                  <FileText className="w-3 h-3 inline mr-0.5" /> Log Receipt
                </button>
                <PermissionGate module="MCI" action="update">
                  <button onClick={() => raiseExpedite(selected.mciLineNumber)}
                    className="px-2 py-1 rounded text-[10px] font-bold text-white" style={{ background: C.accent }}
                    data-testid={`expedite-post-${selected.mciLineNumber}`}>
                    <Zap className="w-3 h-3 inline mr-0.5" /> Expedite
                  </button>
                </PermissionGate>
                <button onClick={() => setSelected(null)} style={{ color: C.slateMuted }}><X className="w-4 h-4" /></button>
              </div>
            </div>

            {/* PO / GRN / IQC summary — mirrors the desktop MCI drawer so
                the drilldown carries the same context the register shows. */}
            {(() => {
              const rt = selected.receiptTotals || {};
              const ordered   = Number(selected.quantity ?? selected.poQuantity ?? 0);
              const grn       = Number(rt.grn ?? 0);
              const pending   = Number(rt.pending ?? 0);
              const accepted  = Number(rt.accepted ?? 0);
              const rejected  = Number(rt.rejected ?? 0);
              const remaining = Math.max(0, ordered - grn);
              const unit      = selected.unit || selected.uom || '';
              const pct       = ordered > 0 ? Math.min(100, (grn / ordered) * 100) : 0;
              // Resolve the *displayed* PO date + its type (Plan / Forecast
              // / Actual). Actual wins if set, else Forecast, else Plan.
              const poType    = selected.poDateType || 'PLAN';
              const poDate    = (poType === 'ACTUAL' && selected.poActualDate)
                  ? selected.poActualDate
                  : (poType === 'FORECAST' && selected.poForecastDate)
                  ? selected.poForecastDate
                  : selected.poDate;
              const rasType   = selected.rasDateType || 'PLAN';
              const rasDate   = (rasType === 'ACTUAL' && selected.rasActualDate)
                  ? selected.rasActualDate
                  : (rasType === 'FORECAST' && selected.rasForecastDate)
                  ? selected.rasForecastDate
                  : selected.requiredAtSiteDate;
              const DateChip = ({ type }) => {
                const cfg = type === 'ACTUAL'   ? { bg: '#10B98120', fg: 'var(--tone-green-text)', lbl: 'Actual' }
                          : type === 'FORECAST' ? { bg: '#F59E0B20', fg: 'var(--tone-amber-text)', lbl: 'Forecast' }
                          :                       { bg: '#1E4E8C15', fg: 'var(--tone-blue-text)', lbl: 'Plan' };
                return (
                  <span className="ml-1 px-1 py-[1px] rounded text-[8.5px] font-bold uppercase tracking-wider"
                    style={{ background: cfg.bg, color: cfg.fg }}>{cfg.lbl}</span>
                );
              };
              // Renders a 3-row Plan · Forecast · Actual block for a
              // header date (PO Date / RAS). The `activeType` value is
              // highlighted so users can see which of the three the
              // system is currently tracking against.
              const DateTriple = ({ label, plan, forecast, actual, activeType, testId }) => {
                const rows = [
                  { key: 'PLAN',     lbl: 'Plan',     val: plan,     fg: 'var(--tone-blue-text)' },
                  { key: 'FORECAST', lbl: 'Forecast', val: forecast, fg: 'var(--tone-amber-text)' },
                  { key: 'ACTUAL',   lbl: 'Actual',   val: actual,   fg: 'var(--tone-green-text)' },
                ];
                return (
                  <div data-testid={testId}>
                    <div className="text-[10px] mb-0.5" style={{ color: C.slateMuted }}>{label}</div>
                    <div className="space-y-0.5">
                      {rows.map(r => {
                        const isActive = r.key === activeType;
                        return (
                          <div key={r.key} className="flex items-center gap-1 text-[10.5px]"
                            style={{ opacity: r.val ? 1 : 0.55 }}
                            data-testid={`${testId}-${r.key.toLowerCase()}`}>
                            <span className="inline-block w-14 uppercase font-semibold tracking-wide"
                              style={{ color: isActive ? r.fg : C.slateMuted }}>
                              {r.lbl}
                            </span>
                            <b className="tabular-nums font-mono" style={{ color: isActive ? r.fg : C.slate }}>
                              {(r.val || '').slice(0, 10) || '—'}
                            </b>
                            {isActive && r.val && (
                              <span className="ml-0.5 px-1 py-[1px] rounded text-[8px] font-bold uppercase tracking-wider"
                                style={{ background: `${r.fg}18`, color: r.fg }}>Tracking</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              };
              return (
                <div className="rounded-lg p-3 mb-3" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}
                  data-testid="drilldown-po-summary">
                  <div className="grid grid-cols-2 gap-3 text-[11px] mb-2" style={{ color: C.slate }}>
                    <div className="space-y-2">
                      <div><span style={{ color: C.slateMuted }}>PO Line:</span> <b style={{ color: C.accent }}>L{selected.poLineItem || '—'}</b></div>
                      <DateTriple label="PO Date"
                        plan={selected.poDate}
                        forecast={selected.poForecastDate || selected.poDate}
                        actual={selected.poActualDate}
                        activeType={poType} testId="po-date-triple" />
                    </div>
                    <div className="space-y-2">
                      <div><span style={{ color: C.slateMuted }}>Location:</span> <b>{selected.defaultReceiptLocation || '—'}</b></div>
                      <DateTriple label="Required at site"
                        plan={selected.requiredAtSiteDate}
                        forecast={selected.rasForecastDate || selected.requiredAtSiteDate}
                        actual={selected.rasActualDate}
                        activeType={rasType} testId="ras-date-triple" />
                    </div>
                  </div>
                  <div className="grid grid-cols-6 gap-1 text-center text-[10px]" data-testid="drilldown-grn-iqc-strip">
                    {[
                      { lbl: 'PO Qty',   val: ordered,   col: C.primaryText },
                      { lbl: 'GRN',      val: grn,       col: 'var(--tone-blue-text)' },
                      { lbl: 'Pending',  val: pending,   col: 'var(--tone-amber-text)' },
                      { lbl: 'Accepted', val: accepted,  col: 'var(--tone-green-text)' },
                      { lbl: 'Rejected', val: rejected,  col: 'var(--tone-red-text)' },
                      { lbl: 'Balance',  val: remaining, col: remaining > 0 ? 'var(--tone-red-text)' : 'var(--tone-green-text)' },
                    ].map(m => (
                      <div key={m.lbl} className="rounded p-1.5" style={{ background: 'var(--card-bg)', border: `1px solid ${C.border}` }}>
                        <div className="text-[8.5px] uppercase font-semibold" style={{ color: C.slateMuted }}>{m.lbl}</div>
                        <div className="text-[13px] font-bold tabular-nums" style={{ color: m.col }}>{m.val}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-[9.5px] mb-0.5" style={{ color: C.slateMuted }}>
                      <span>GRN progress</span>
                      <span className="font-mono">{pct.toFixed(0)}% · {grn}/{ordered} {unit}</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                      <div className="h-full transition-all" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${C.primary}, #10B981)` }} />
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* All receipts (all GRNs) + Related PO Lines */}
            <MciReceiptsAndRelated projectId={projectId} mci={selected} />

            <div className="text-[10px] uppercase font-semibold mb-2 tracking-[0.08em]" style={{ color: C.primaryText }}>Post-Order Gates <span style={{ color: C.slateMuted }} className="normal-case font-normal">— one full chain per accepted receipt, plus the open balance</span></div>
            {(() => {
              const canonList = (selected.gates || []).map(g => ({
                name: g.name, planned: g.planned, forecast: g.forecast,
              }));
              const liveByName = new Map((selected.gates || []).map(g => [g.name, g]));

              const rt = selected.receiptTotals || {};
              const orderedQty  = Number(selected.quantity ?? selected.poQuantity ?? 0);
              const grnDone     = Number(rt.grn ?? 0);
              const remaining   = Math.max(0, orderedQty - grnDone);
              const fullyDelivered = orderedQty > 0 && remaining <= 0.001;
              const unit = selected.unit || '';

              const acceptedReceipts = (selected.receipts || [])
                .filter(r => (r.acceptedQty || 0) > 0)
                .sort((a, b) => String(a.receiptDate || '').localeCompare(String(b.receiptDate || '')));

              const buckets = [];
              acceptedReceipts.forEach(r => {
                const ledgerByName = new Map((r.receiptGateLedger || []).map(x => [x.stageGateName, x]));
                const gates = canonList.map(c => {
                  const inf = ledgerByName.get(c.name);
                  const live = liveByName.get(c.name) || {};
                  const actual = live.actual || inf?.inferredActualDate || r.receiptDate;
                  return {
                    name: c.name, planned: c.planned, forecast: c.forecast, actual,
                    status: 'DONE',
                    inspectionResult: live.inspectionResult,
                    inspectionReportRef: live.inspectionReportRef,
                    shippingTrackingRef: live.shippingTrackingRef,
                    shippingCarrier: live.shippingCarrier,
                    grnNumber: live.grnNumber,
                    remarks: live.remarks,
                    _inferred: !live.actual,
                  };
                });
                buckets.push({
                  key: r.receiptNumber || `RCP-${r.receiptDate}`,
                  isOpen: false, qty: r.acceptedQty, unit: r.unit || unit,
                  receiptDate: r.receiptDate, poNumber: selected.poNumber,
                  poLineItem: selected.poLineItem, location: r.acceptedLocation,
                  gates,
                });
              });

              if (!fullyDelivered) {
                const gates = canonList.map(c => {
                  const live = liveByName.get(c.name) || {};
                  return {
                    name: c.name, planned: c.planned, forecast: c.forecast,
                    actual: live.actual,
                    status: live.status || (live.actual ? 'DONE' : 'ON_TRACK'),
                    inspectionResult: live.inspectionResult,
                    inspectionReportRef: live.inspectionReportRef,
                    shippingTrackingRef: live.shippingTrackingRef,
                    shippingCarrier: live.shippingCarrier,
                    grnNumber: live.grnNumber, remarks: live.remarks,
                  };
                });
                buckets.push({
                  key: 'OPEN_BALANCE', isOpen: true, qty: remaining, unit,
                  poNumber: selected.poNumber, poLineItem: selected.poLineItem,
                  gates,
                });
              }

              if (buckets.length === 0) {
                return (
                  <div className="p-3 rounded-lg text-[11px] text-center italic"
                    style={{ background: 'var(--card-bg-alt)', color: C.slateMuted, border: `1px dashed ${C.border}` }}>
                    No post-order gates on this MCI yet.
                  </div>
                );
              }

              return (
                <div className="space-y-3" data-testid="drilldown-gate-buckets">
                  {buckets.map(b => (
                    <div key={b.key} className="rounded-lg overflow-hidden"
                      style={{ border: `1px solid ${b.isOpen ? '#F59E0B60' : '#10B98160'}` }}
                      data-testid={`gate-bucket-${b.key}`}>
                      <div className="px-2 py-1.5 flex items-center justify-between flex-wrap gap-1"
                        style={{ background: b.isOpen ? 'var(--tone-amber-bg)' : 'var(--tone-green-bg)' }}>
                        <div className="flex items-center gap-2 text-[10px] flex-wrap">
                          <span className="px-1.5 py-0.5 rounded font-bold uppercase tracking-wide"
                            style={{ background: b.isOpen ? '#F59E0B' : '#10B981', color: '#fff' }}>
                            {b.isOpen ? 'Open Balance' : 'Accepted Receipt'}
                          </span>
                          <span className="font-mono font-bold" style={{ color: 'var(--tone-blue-text)' }}>{b.poNumber || '—'}</span>
                          <span style={{ color: C.slateMuted }}>·</span>
                          <span className="font-mono" style={{ color: C.slate }}>Line L{b.poLineItem || '—'}</span>
                          <span style={{ color: C.slateMuted }}>·</span>
                          <span className="font-mono font-bold" style={{ color: b.isOpen ? 'var(--tone-amber-text)' : 'var(--tone-green-text)' }}>
                            {b.qty} {b.unit}
                          </span>
                          {!b.isOpen && (
                            <>
                              <span style={{ color: C.slateMuted }}>·</span>
                              <span className="font-mono" style={{ color: 'var(--tone-green-text)' }}>{b.key}</span>
                              {b.receiptDate && (<><span style={{ color: C.slateMuted }}>·</span>
                                <span style={{ color: 'var(--tone-green-text)' }}>received {b.receiptDate.slice(0, 10)}</span></>)}
                              {b.location && (<><span style={{ color: C.slateMuted }}>·</span>
                                <span style={{ color: 'var(--tone-green-text)' }}>stored at {b.location}</span></>)}
                            </>
                          )}
                        </div>
                        <span className="text-[9.5px]" style={{ color: C.slateMuted }}>{b.gates.length} gates</span>
                      </div>
                      <div className="space-y-1 p-1.5">
                        {b.gates.map((g, i) => {
                          const dateType = g.actual ? 'ACTUAL' : g.forecast ? 'FORECAST' : 'PLAN';
                          const displayDate = g.actual || g.forecast || g.planned;
                          const dateColor = dateType === 'ACTUAL' ? '#10B981' : dateType === 'FORECAST' ? '#F59E0B' : '#1E4E8C';
                          const dateLbl = dateType === 'ACTUAL' ? (g._inferred ? 'Inferred Actual' : 'Actual')
                                        : dateType === 'FORECAST' ? 'Forecast' : 'Plan';
                          const badgeBg = STATUS[g.status] || 'var(--text-muted)';
                          const badgeText = g.status?.replace('_', ' ') || '—';
                          return (
                            <div key={i} className="p-2 rounded text-[11px]"
                              style={{ background: C.surfaceAlt, border: `1px solid ${badgeBg}55` }}>
                              <div className="flex items-center justify-between">
                                <div className="font-semibold" style={{ color: C.slate }}>{g.name}</div>
                                <span className="px-1.5 rounded text-[9px] font-bold text-white" style={{ background: badgeBg }}>{badgeText}</span>
                              </div>
                              <div className="text-[10px] mt-1" style={{ color: C.slateMuted }}>
                                Planned: <b>{(g.planned || '').slice(0, 10) || '—'}</b> · Forecast: <b>{(g.forecast || '').slice(0, 10) || '—'}</b> · Actual: <b>{(g.actual || '').slice(0, 10) || '—'}</b>
                              </div>
                              {displayDate && (
                                <div className="text-[9.5px] mt-0.5" style={{ color: dateColor }}>
                                  <b>Currently tracking:</b> {displayDate.slice(0, 10)}
                                  <span className="ml-1 px-1 py-[1px] rounded font-bold uppercase tracking-wider"
                                    style={{ background: `${dateColor}22`, color: dateColor }}>{dateLbl}</span>
                                </div>
                              )}
                              {(g.inspectionResult || g.shippingTrackingRef || g.grnNumber || g.inspectionReportRef) && (
                                <div className="text-[10px] mt-0.5" style={{ color: C.slateMuted }}>
                                  {g.inspectionResult && <>Inspection: <b style={{ color: g.inspectionResult === 'PASS' ? '#22C55E' : '#EF4444' }}>{g.inspectionResult}</b> · </>}
                                  {g.inspectionReportRef && <><a href={g.inspectionReportRef} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 underline" style={{ color: C.primaryText }}><FileText className="w-2.5 h-2.5" /> report</a> · </>}
                                  {g.shippingTrackingRef && (
                                    <>Tracking: <a
                                      href={g.shippingCarrier?.url || `https://track.aftership.com/${g.shippingTrackingRef}`}
                                      target="_blank" rel="noreferrer" className="underline" style={{ color: C.primaryText }}
                                      data-testid={`tracking-${selected.mciLineNumber}`}>
                                      {g.shippingTrackingRef} {g.shippingCarrier?.carrier && `(${g.shippingCarrier.carrier})`}
                                    </a> · </>
                                  )}
                                  {g.grnNumber && <>GRN: <b>{g.grnNumber}</b></>}
                                </div>
                              )}
                              {g.remarks && <div className="text-[10px] mt-0.5 italic" style={{ color: C.slateMuted }}>{g.remarks}</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="hidden">
              {selected.gates.map((g, i) => (
                <div key={i} className="p-2 rounded text-[11px]"
                  style={{ background: C.surfaceAlt, border: `1px solid ${STATUS[g.status] || C.border}55` }}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold" style={{ color: C.slate }}>{g.name}</div>
                    <span className="px-1.5 rounded text-[9px] font-bold text-white" style={{ background: STATUS[g.status] }}>{g.status?.replace('_', ' ')}</span>
                  </div>
                  <div className="text-[10px] mt-1" style={{ color: C.slateMuted }}>
                    Planned: {g.planned?.slice(0, 10) || '—'} · Forecast: {g.forecast?.slice(0, 10) || '—'} · Actual: {g.actual?.slice(0, 10) || '—'}
                  </div>
                  {(g.inspectionResult || g.shippingTrackingRef || g.grnNumber || g.inspectionReportRef) && (
                    <div className="text-[10px] mt-0.5" style={{ color: C.slateMuted }}>
                      {g.inspectionResult && <>Inspection: <b style={{ color: g.inspectionResult === 'PASS' ? '#22C55E' : '#EF4444' }}>{g.inspectionResult}</b> · </>}
                      {g.inspectionReportRef && <><a href={g.inspectionReportRef} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 underline" style={{ color: C.primaryText }}><FileText className="w-2.5 h-2.5" /> report</a> · </>}
                      {g.shippingTrackingRef && (
                        <>Tracking: <a
                          href={g.shippingCarrier?.url || `https://track.aftership.com/${g.shippingTrackingRef}`}
                          target="_blank" rel="noreferrer" className="underline" style={{ color: C.primaryText }}
                          data-testid={`tracking-${selected.mciLineNumber}`}>
                          {g.shippingTrackingRef} {g.shippingCarrier?.carrier && `(${g.shippingCarrier.carrier})`}
                        </a> · </>
                      )}
                      {g.grnNumber && <>GRN: <b>{g.grnNumber}</b></>}
                    </div>
                  )}
                  {g.remarks && <div className="text-[10px] mt-0.5 italic" style={{ color: C.slateMuted }}>{g.remarks}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {vendor && <VendorDrilldown projectId={projectId} vendor={vendor} onClose={() => setVendor(null)} />}
      {exception && <ExceptionDrilldown type={exception} items={data.exceptionDetails?.[exception] || []} onClose={() => setException(null)} />}
      {bulkOpen && (
        <BulkForecastReview projectId={projectId} rows={bulkRows}
          title="Cognara Forecast · Post-Order Register"
          onClose={() => setBulkOpen(false)} onApplied={() => load()} />
      )}
      {drilldownReceipts && (
        <LogReceiptDrawer projectId={projectId} mci={drilldownReceipts.mciLineNumber}
          unit={drilldownReceipts.unit || drilldownReceipts.uom}
          scope={drilldownReceipts.quantity ?? drilldownReceipts.poQuantity ?? 0}
          defaultLocation={drilldownReceipts.defaultReceiptLocation}
          onClose={() => setDrilldownReceipts(null)}
          onChanged={() => load()} />
      )}
      {receiptDrilldown && (
        <ReceiptDrilldownModal projectId={projectId}
          mciLineNumber={receiptDrilldown.mci}
          poNumber={receiptDrilldown.poNumber}
          poLineItem={receiptDrilldown.poLineItem}
          receipt={receiptDrilldown.receipt}
          remaining={receiptDrilldown.remaining}
          unit={receiptDrilldown.unit}
          onClose={() => setReceiptDrilldown(null)} />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// All Receipts (every GRN) + Related PO Lines — rendered inside the
// post-order drilldown. Each receipt is expandable to show its own
// milestone timeline (arrival → IQC → accepted / rejected).
// ─────────────────────────────────────────────────────────────────
function MciReceiptsAndRelated({ projectId, mci }) {
  const [tab, setTab] = useState('RECEIPTS');
  const [receipts, setReceipts] = useState(null);
  const [related, setRelated] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!mci?.mciLineNumber) return;
    (async () => {
      try {
        const [rRes, relRes] = await Promise.all([
          api.get(`/v1/projects/${projectId}/mci/entries/${mci.mciLineNumber}/receipts`),
          api.get(`/v1/projects/${projectId}/mci/entries/${mci.mciLineNumber}/related`),
        ]);
        setReceipts(rRes.data);
        setRelated(relRes.data);
      } catch (_) { /* silent — drilldown still usable */ }
    })();
  }, [projectId, mci?.mciLineNumber]);

  const unit = receipts?.unit || mci.unit || '';

  return (
    <div className="mb-3">
      <div className="flex items-center gap-1 mb-2">
        {[
          { id: 'RECEIPTS', lbl: `All Receipts${receipts ? ` (${receipts.receipts.length})` : ''}`, I: PackageCheck },
          { id: 'RELATED',  lbl: `Related PO Lines${related ? ` (${(related.related || []).length})` : ''}`, I: Link2 },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide rounded"
            style={{
              background: tab === t.id ? C.primaryText : C.surfaceAlt,
              color: tab === t.id ? '#fff' : C.slate,
              border: `1px solid ${tab === t.id ? C.primaryText : C.border}`,
            }}
            data-testid={`drilldown-tab-${t.id.toLowerCase()}`}>
            <t.I className="w-3 h-3 inline mr-0.5" /> {t.lbl}
          </button>
        ))}
      </div>

      {/* RECEIPTS TAB */}
      {tab === 'RECEIPTS' && (
        receipts === null ? (
          <div className="text-[10px] italic p-3" style={{ color: C.slateMuted }}>Loading receipts…</div>
        ) : receipts.receipts.length === 0 ? (
          <div className="text-[11px] p-3 rounded" style={{ background: C.surfaceAlt, color: C.slateMuted }}>
            No receipts logged yet for this PO line. Use <b>Log Receipt</b> above to start the GRN chain.
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="drilldown-receipts-list">
            {receipts.receipts.slice().sort((a, b) => (b.receiptDate || '').localeCompare(a.receiptDate || '')).map(r => {
              const isOpen = !!expanded[r.receiptNumber];
              const acc = Number(r.acceptedQty || 0), rej = Number(r.rejectedQty || 0), pen = Number(r.iqcQty || 0);
              const grn = Number(r.grnQty || acc + rej + pen);
              const isFullyAccepted = acc > 0 && pen === 0 && rej === 0;
              const isFullyRejected = rej > 0 && acc === 0;
              const badgeColor = isFullyAccepted ? '#10B981' : isFullyRejected ? '#EF4444' : pen > 0 ? '#F59E0B' : C.slateMuted;
              const badgeText  = isFullyAccepted ? 'ACCEPTED' : isFullyRejected ? 'REJECTED' : pen > 0 ? 'PENDING IQC' : 'PARTIAL';
              return (
                <div key={r.receiptNumber} className="rounded"
                  style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}
                  data-testid={`drilldown-receipt-${r.receiptNumber}`}>
                  <button onClick={() => setExpanded(x => ({ ...x, [r.receiptNumber]: !x[r.receiptNumber] }))}
                    className="w-full flex items-center gap-2 p-2 text-left">
                    {isOpen ? <ChevronDown className="w-3.5 h-3.5" style={{ color: C.slateMuted }} /> : <ChevronRight className="w-3.5 h-3.5" style={{ color: C.slateMuted }} />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 text-[11px]">
                        <span className="font-mono font-bold" style={{ color: C.accent }}>{r.receiptNumber}</span>
                        <span style={{ color: C.slateMuted }}>·</span>
                        <span style={{ color: C.slateMuted }}>{(r.receiptDate || '').slice(0, 10)}</span>
                        <span style={{ color: C.slateMuted }}>·</span>
                        <span style={{ color: C.slate }}>{r.acceptedLocation || r.warehouseLocation || '—'}</span>
                      </div>
                    </div>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold text-white" style={{ background: badgeColor }}>{badgeText}</span>
                  </button>
                  {/* KPI strip always visible */}
                  <div className="grid grid-cols-5 gap-1 px-2 pb-2">
                    {[
                      { l: 'GRN',      v: grn, c: 'var(--tone-blue-text)' },
                      { l: 'Pending',  v: pen, c: '#F59E0B' },
                      { l: 'Accepted', v: acc, c: '#10B981' },
                      { l: 'Rejected', v: rej, c: '#EF4444' },
                      { l: 'Location', v: (r.acceptedLocation || '—').replace(/^[A-Z]{3}-BIN · /, ''), c: C.slate, isText: true },
                    ].map(m => (
                      <div key={m.l} className="rounded p-1 text-center" style={{ background: 'var(--card-bg)', border: `1px solid ${C.border}` }}>
                        <div className="text-[8.5px] uppercase font-semibold" style={{ color: C.slateMuted }}>{m.l}</div>
                        <div className={`text-${m.isText ? '[9.5px] font-mono' : '[12px] font-bold tabular-nums'}`} style={{ color: m.c }}>
                          {m.isText ? m.v : `${m.v}${unit ? ' ' + unit : ''}`}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Per-receipt milestone timeline (visible when expanded) */}
                  {isOpen && (
                    <div className="border-t px-3 py-2" style={{ borderColor: C.border }}
                      data-testid={`drilldown-receipt-timeline-${r.receiptNumber}`}>
                      <div className="text-[9px] uppercase font-bold tracking-wide mb-2" style={{ color: C.primaryText }}>Receipt Timeline</div>
                      <ol className="space-y-1.5 pl-1">
                        {[
                          { lbl: 'GRN raised',     date: r.receiptDate || r.createdAt, note: r.documentRef ? `Doc: ${r.documentRef}` : null, ok: !!(r.receiptDate || r.createdAt) },
                          { lbl: 'Delivered',      date: r.deliveredAt || r.receiptDate, note: r.inspectedBy ? `Received by ${r.inspectedBy}` : null, ok: grn > 0 },
                          { lbl: 'IQC inspection', date: r.inspectionDate || (acc + rej > 0 ? r.receiptDate : null), note: r.inspectionReportRef ? `Report: ${r.inspectionReportRef}` : null, ok: acc + rej > 0 },
                          { lbl: acc > 0 ? 'Accepted → Inventory' : 'Awaiting acceptance', date: acc > 0 ? (r.acceptedAt || r.receiptDate) : null, note: acc > 0 ? `${acc} ${unit} → ${r.acceptedLocation || '—'}` : null, ok: acc > 0 },
                          ...(rej > 0 ? [{ lbl: 'Rejected',    date: r.rejectedAt || r.receiptDate,  note: `${rej} ${unit} → ${r.rejectedLocation || 'quarantine'}`,  ok: false }] : []),
                        ].map((s, i, arr) => (
                          <li key={i} className="relative pl-4 text-[10px]">
                            <span className="absolute left-0 top-1 w-2 h-2 rounded-full" style={{ background: s.ok ? '#10B981' : s.date ? '#F59E0B' : C.border }} />
                            {i < arr.length - 1 && <span className="absolute left-[3px] top-3 bottom-[-8px] w-px" style={{ background: C.border }} />}
                            <div className="flex justify-between gap-2">
                              <span className="font-semibold" style={{ color: C.slate }}>{s.lbl}</span>
                              <span className="font-mono" style={{ color: s.ok ? '#10B981' : C.slateMuted }}>{(s.date || '').slice(0, 10) || 'pending'}</span>
                            </div>
                            {s.note && <div className="italic" style={{ color: C.slateMuted }}>{s.note}</div>}
                          </li>
                        ))}
                      </ol>
                      {r.inspectionRemarks && (
                        <div className="mt-2 p-1.5 rounded text-[10px] italic"
                          style={{ background: 'var(--tone-amber-bg)', color: 'var(--tone-amber-text)' }}>
                          IQC remark: {r.inspectionRemarks}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {/* Totals footer */}
            <div className="grid grid-cols-5 gap-1 mt-2 p-2 rounded" style={{ background: C.primary + '10', border: `1px solid ${C.primary}30` }}>
              {[
                { l: 'Σ GRN',      v: receipts.totals.grn },
                { l: 'Σ Pending',  v: receipts.totals.iqc },
                { l: 'Σ Accepted', v: receipts.totals.accepted, c: '#10B981' },
                { l: 'Σ Rejected', v: receipts.totals.rejected, c: '#EF4444' },
                { l: 'Balance',    v: receipts.totals.remaining, c: receipts.totals.remaining > 0 ? '#EF4444' : '#10B981' },
              ].map(m => (
                <div key={m.l} className="text-center">
                  <div className="text-[8.5px] uppercase font-bold" style={{ color: C.slateMuted }}>{m.l}</div>
                  <div className="text-[12px] font-bold tabular-nums" style={{ color: m.c || C.primaryText }}>{Number(m.v || 0)}{unit ? ' ' + unit : ''}</div>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {/* RELATED PO LINES TAB */}
      {tab === 'RELATED' && (
        related === null ? (
          <div className="text-[10px] italic p-3" style={{ color: C.slateMuted }}>Loading related PO lines…</div>
        ) : (related.related || []).length === 0 ? (
          <div className="text-[11px] p-3 rounded" style={{ background: C.surfaceAlt, color: C.slateMuted }}>
            No other MCIs share this material code ({related.sourceMaterialCode || 'n/a'}) or vendor ({related.sourceVendor || 'n/a'}).
          </div>
        ) : (
          <>
            <div className="text-[10px] mb-1.5" style={{ color: C.slateMuted }}>
              <b>{related.sameCodeCount}</b> other line(s) share <b>{related.sourceMaterialCode || 'material code'}</b> ·
              <b> {related.sameVendorCount}</b> share vendor <b>{related.sourceVendor || 'n/a'}</b>
            </div>
            <div className="rounded overflow-hidden" style={{ border: `1px solid ${C.border}` }}
              data-testid="drilldown-related-list">
              <table className="w-full text-[11px]">
                <thead style={{ background: C.surfaceAlt }}>
                  <tr>
                    <th className="p-1.5 text-left" style={{ color: C.slateMuted }}>MCI</th>
                    <th className="p-1.5 text-left" style={{ color: C.slateMuted }}>PO · Line</th>
                    <th className="p-1.5 text-right" style={{ color: C.slateMuted }}>Qty</th>
                    <th className="p-1.5 text-right" style={{ color: C.slateMuted }}>GRN</th>
                    <th className="p-1.5 text-right" style={{ color: '#10B981' }}>Accepted</th>
                    <th className="p-1.5 text-left" style={{ color: C.slateMuted }}>Match</th>
                  </tr>
                </thead>
                <tbody>
                  {related.related.map(r => (
                    <tr key={r.mciLineNumber} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td className="p-1.5 font-mono font-bold" style={{ color: C.primaryText }}>{r.mciLineNumber}</td>
                      <td className="p-1.5 font-mono text-[10px]" style={{ color: C.slate }}>
                        {r.poNumber || '—'}<span style={{ color: C.slateMuted }}> · L{r.poLineItem || '?'}</span>
                        <div className="text-[9px] truncate max-w-[200px]" style={{ color: C.slateMuted }} title={r.materialDescription}>
                          {r.materialDescription}
                        </div>
                      </td>
                      <td className="p-1.5 text-right font-mono" style={{ color: C.slate }}>{r.quantity}<span style={{ color: C.slateMuted }}> {r.unit}</span></td>
                      <td className="p-1.5 text-right font-mono" style={{ color: 'var(--tone-blue-text)' }}>{r.totalGrn}</td>
                      <td className="p-1.5 text-right font-mono font-bold" style={{ color: '#10B981' }}>{r.totalAccepted}</td>
                      <td className="p-1.5 text-[9px]">
                        {r.sharesCode && (
                          <span className="inline-block px-1.5 py-0.5 rounded mr-0.5 font-bold" style={{ background: 'var(--tone-blue-bg)', color: 'var(--tone-blue-text)' }}>CODE</span>
                        )}
                        {r.sharesVendor && (
                          <span className="inline-block px-1.5 py-0.5 rounded font-bold" style={{ background: 'var(--tone-pink-bg)', color: 'var(--tone-pink-text)' }}>VENDOR</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      )}
    </div>
  );
}
